import { Pill, StatusDot } from '@/design-system';
import type { HierarchyLevel } from '@/shared/types';
import { usePersona } from '@/app/providers';
import { PLANT } from '@/versions/v1/shared/bunker-hill-seed';
import { activeCrewLabel } from '@/versions/v1/shared/bunker-hill-org-seed';
import { plantStartupBadgeLabel } from '@/versions/v1/shared/plant.transform';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';

import { SessionClockControl } from '@/versions/v1/shared/SessionClockControl';
import { ForPartner, PRODUCT_NAME } from '@/versions/v1/shared/brand/PartnerMark';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import {
  productBarIdentity,
  productStatusChips,
} from './ProductTopBar.transform';

export function ProductTopBar({ level }: { level: HierarchyLevel }) {
  const { persona } = usePersona();
  const { state, stepCursor, setCursor } = useScenario();

  if (level !== 'project' && level !== 'entity') return null;

  const identity = productBarIdentity(persona);
  const statusChips = productStatusChips(state.cadence);


  return (
    <div
      role="region"
      aria-label="Product chrome"
      data-comment-anchor="ProductTopBar"
      className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-4 py-2 text-sm text-zinc-700 dark:text-zinc-200"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-[0.35em] font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
          {PRODUCT_NAME}
          <ForPartner height={11} />
        </span>
        <span className="text-muted" aria-hidden="true">·</span>
        <span className="truncate font-medium text-zinc-900 dark:text-zinc-100">
          {PLANT.name}
        </span>
        <Pill tone="default">{plantStartupBadgeLabel()}</Pill>
      </div>

      <div className="hidden min-w-0 flex-1 items-center justify-center gap-x-2 text-muted md:flex">
        <SessionClockControl

          cursor={state.cursor}
          onStep={stepCursor}
          onReset={() => setCursor(DEFAULT_CURSOR)}
        />
        <span aria-hidden="true">·</span>
        <span className="shrink-0">{activeCrewLabel(state.cursor)}</span>
      </div>

      <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
        <div className="hidden flex-wrap items-center gap-x-2 gap-y-1 text-muted lg:flex">
          {statusChips.map((chip) => (
            <span key={chip.label} className="inline-flex items-center gap-1.5" title={chip.title ?? chip.label}>
              <StatusDot state={chip.state} />
              <span className={chip.state === 'online' && !chip.labelVisible ? 'sr-only' : 'text-xs'}>{chip.label}</span>
            </span>
          ))}
        </div>
        <span
          className="inline-flex items-center gap-2 rounded-full border border-[var(--glass-border)] bg-[var(--glass-surface)] px-2.5 py-1 pl-1.5 pr-3 text-xs font-medium text-zinc-900 dark:text-zinc-100"
          title={identity.title}
        >
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold uppercase text-zinc-800 dark:bg-zinc-700 dark:text-zinc-100"
            aria-hidden="true"
          >
            {identity.initial}
          </span>
          <span className="truncate">{identity.label}</span>
        </span>
      </div>
    </div>
  );
}
